/**
 * Gauntlet Debates - A platform for structured, AI-moderated honorable debates
 * Copyright (c) 2025 Gauntlet Debates. All rights reserved.
 * 
 * This file is part of the Gauntlet Debates platform and is protected by 
 * copyright and other intellectual property laws. Unauthorized reproduction,
 * distribution, or disclosure of this file or its contents is prohibited.
 */

import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';

const TermsOfServicePage: React.FC = () => {
  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Typography variant="h3" component="h1" gutterBottom align="center">
        Terms of Service
      </Typography>
      
      <Typography variant="subtitle1" color="text.secondary" paragraph align="center" sx={{ mb: 6 }}>
        Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
      </Typography>

      <Paper sx={{ p: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            1. Introduction
          </Typography>
          <Typography paragraph>
            Welcome to Gauntlet Debates™ ("we," "our," or "us"). These Terms of Service ("Terms") govern your access to and use of the Gauntlet Debates™ platform, including any content, functionality, and services offered on or through gauntletdebates.com (the "Platform").
          </Typography>
          <Typography paragraph>
            By using the Platform, you accept and agree to be bound by these Terms. If you do not agree to these Terms, you must not access or use the Platform.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            2. Intellectual Property Rights
          </Typography>
          <Typography paragraph>
            The Platform and its entire contents, features, and functionality (including but not limited to all information, software, text, displays, images, and the design, selection, and arrangement thereof) are owned by Gauntlet Debates, its licensors, or other providers of such material and are protected by copyright, trademark, patent, trade secret, and other intellectual property or proprietary rights laws.
          </Typography>
          <Typography paragraph>
            <strong>Proprietary Elements:</strong> The following elements of the Platform are proprietary to Gauntlet Debates and protected by intellectual property laws:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Jester's Gauntlet™ System" 
                secondary="Our unique methodology and algorithms for detecting debate fallacies and argumentative misconduct."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Structured Debate Format" 
                secondary="The specific framework and methodology used to conduct honorable debates on our platform."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Honor-Based Scoring System and Leaderboard" 
                secondary="Our comprehensive methods for evaluating debate quality, assigning penalties, determining outcomes, and ranking participants based on their performance."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="AI Moderation and Analysis" 
                secondary="Our proprietary approaches to moderating debates and generating summaries."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Knowledge Repository" 
                secondary="Our searchable repository of debate summaries and best explanations that serves as a resource for understanding complex topics."
              />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            3. Prohibited Uses
          </Typography>
          <Typography paragraph>
            You may use the Platform only for lawful purposes and in accordance with these Terms. You agree not to use the Platform:
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="In any way that violates any applicable federal, state, local, or international law or regulation." />
            </ListItem>
            <ListItem>
              <ListItemText primary="To send, knowingly receive, upload, download, use, or re-use any material that violates these Terms." />
            </ListItem>
            <ListItem>
              <ListItemText primary="To engage in any conduct that restricts or inhibits anyone's use or enjoyment of the Platform." />
            </ListItem>
            <ListItem>
              <ListItemText primary="To copy, adapt, modify, create derivative works from, reverse engineer, decompile, or attempt to extract the source code of the Platform or its algorithms." />
            </ListItem>
            <ListItem>
              <ListItemText primary="To reproduce, duplicate, copy, sell, resell, or exploit any portion of the Platform without express written permission from Gauntlet Debates." />
            </ListItem>
            <ListItem>
              <ListItemText primary="To scrape, collect, or harvest any data from the Platform beyond normal user interactions." />
            </ListItem>
            <ListItem>
              <ListItemText primary="To impersonate or attempt to impersonate Gauntlet Debates, a Gauntlet Debates employee, another user, or any other person or entity." />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            4. User Content and Conduct
          </Typography>
          <Typography paragraph>
            By submitting content to the Platform, including debate messages, arguments, and any other materials (collectively, "User Content"), you grant Gauntlet Debates a worldwide, non-exclusive, royalty-free license to use, reproduce, adapt, publish, and distribute such User Content for the purposes of operating and improving the Platform.
          </Typography>
          <Typography paragraph>
            You represent and warrant that:
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="You own or control all rights to the User Content you post or have the right to grant the license above." />
            </ListItem>
            <ListItem>
              <ListItemText primary="Your User Content does not violate the privacy rights, publicity rights, copyrights, contract rights, or any other rights of any person or entity." />
            </ListItem>
            <ListItem>
              <ListItemText primary="Your User Content does not contain any material that is defamatory, obscene, indecent, abusive, offensive, harassing, violent, hateful, inflammatory, or otherwise objectionable." />
            </ListItem>
          </List>
          <Typography paragraph>
            Gauntlet Debates reserves the right to remove any User Content that violates these Terms or that we determine, in our sole discretion, is harmful, offensive, or otherwise inappropriate.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            5. Debate Rules and Flagging System
          </Typography>
          <Typography paragraph>
            The Platform includes a system for detecting debate fallacies and misconduct, referred to as the "Jester's Gauntlet™" system. By using the Platform, you agree to abide by the debate rules and accept the determinations made by this system, subject to the flag review mechanisms provided on the Platform.
          </Typography>
          <Typography paragraph>
            You understand and agree that:
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="AI moderation may flag messages that contain potential fallacies or debate misconduct." />
            </ListItem>
            <ListItem>
              <ListItemText primary="Opposing teams may lift flags or request reformulations as provided for in the debate rules." />
            </ListItem>
            <ListItem>
              <ListItemText primary="Accumulation of penalties may result in debate forfeiture as specified in the debate rules." />
            </ListItem>
            <ListItem>
              <ListItemText primary="The AI may generate summaries and determine outcomes based on the content and quality of the debate." />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            6. Content Repository and Licensing
          </Typography>
          <Typography paragraph>
            Gauntlet Debates aims to create a repository of high-quality debate summaries and arguments. By participating in public debates on the Platform, you acknowledge and agree that:
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="Summaries of public debates will be stored in our repository and may be accessible to other users." />
            </ListItem>
            <ListItem>
              <ListItemText primary="Such summaries may be used to build a knowledge base on various topics for educational and informational purposes." />
            </ListItem>
            <ListItem>
              <ListItemText primary="Your contributions to public debates may be included in these summaries, with appropriate attribution where possible." />
            </ListItem>
          </List>
          <Typography paragraph>
            The Knowledge Repository and its organization, search capabilities, filtering mechanisms, categorization systems, and presentation methods are proprietary to Gauntlet Debates. While the content may include user contributions, the repository system itself, including its architecture and functionality, is protected intellectual property.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            7. Disclaimer of Warranties
          </Typography>
          <Typography paragraph>
            THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT ANY WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. GAUNTLET DEBATES DISCLAIMS ALL WARRANTIES, INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
          </Typography>
          <Typography paragraph>
            Gauntlet Debates does not guarantee the accuracy, completeness, or reliability of any content or communications on the Platform, including AI-generated flags, summaries, or determinations.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            8. Limitation of Liability
          </Typography>
          <Typography paragraph>
            IN NO EVENT WILL GAUNTLET DEBATES, ITS AFFILIATES, OR THEIR LICENSORS, SERVICE PROVIDERS, EMPLOYEES, AGENTS, OFFICERS, OR DIRECTORS BE LIABLE FOR DAMAGES OF ANY KIND ARISING OUT OF OR IN CONNECTION WITH YOUR USE OF THE PLATFORM.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            9. Changes to Terms of Service
          </Typography>
          <Typography paragraph>
            Gauntlet Debates reserves the right to modify or replace these Terms at any time. If a revision is material, we will provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box>
          <Typography variant="h5" gutterBottom>
            10. Contact Information
          </Typography>
          <Typography paragraph>
            Questions about the Terms of Service should be sent to us at vincent@gauntletdebates.com.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default TermsOfServicePage; 